import jsPDF from 'jspdf';
import { Contrat } from '../types';
import { LOGO_BASE64, CACHET_BASE64, GRIFFE_BASE64, SIGNATURE_BASE64 } from './companyAssets';

interface ContratPDFData extends Contrat {
  telephone?: string;
  email?: string;
  wilaya?: string;
  ville?: string;
}

export function generateContratPDF(contrat: ContratPDFData) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginLeft = 20;
  const marginRight = 20;
  const contentWidth = pageWidth - marginLeft - marginRight;
  let y = 20;

  // --- Helper functions ---
  const centerText = (text: string, yPos: number, fontSize: number, style: 'normal' | 'bold' | 'italic' | 'bolditalic' = 'normal') => {
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', style);
    doc.text(text, pageWidth / 2, yPos, { align: 'center' });
  };

  const drawLine = (yPos: number) => {
    doc.setDrawColor(22, 45, 87);
    doc.setLineWidth(0.5);
    doc.line(marginLeft, yPos, pageWidth - marginRight, yPos);
  };

  const addLabelValue = (label: string, value: string, yPos: number): number => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(label, marginLeft + 5, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(value, marginLeft + 55, yPos);
    return yPos + 6;
  };

  // --- Format dates ---
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const dateDebut = formatDate(contrat.date_debut);
  const dateFin = formatDate(contrat.date_fin);
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

  // ===== HEADER (sans fond, economique en encre) =====
  // Logo en haut a gauche (agrandi : 46x43mm)
  try {
    doc.addImage(LOGO_BASE64, 'PNG', marginLeft - 10, 2, 46, 43);
  } catch {
    // Ignore si le logo ne peut etre charge
  }

  // Textes centres en bleu fonce (#162D57)
  doc.setTextColor(22, 45, 87);
  // Denomination (style italic bold, rappelle la police du logo)
  centerText('BOUCHAIR Abdelouahab', 16, 20, 'bolditalic');
  // Activite
  centerText("Bureau d'etudes et de consultation en Informatique", 24, 9, 'normal');
  // Adresse
  centerText('11 Rue Rekaif Ali, Setif', 30, 8.5, 'normal');
  // Contact
  centerText('Email: contact@winalco.com  |  Tel: 0661702451', 35, 8.5, 'normal');

  // Reset text color (sub-title supprime pour gagner de l'espace)
  doc.setTextColor(0, 0, 0);
  y = 42;

  // ===== TITLE =====
  doc.setFillColor(240, 244, 248);
  doc.roundedRect(marginLeft, y, contentWidth, 9, 2, 2, 'F');
  doc.setTextColor(22, 45, 87);
  centerText('CONTRAT DE SERVICE', y + 6.5, 13, 'bold');
  doc.setTextColor(0, 0, 0);
  y += 12;

  // Contract reference & date
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Ref: CTR-${String(contrat.id).padStart(5, '0')}`, marginLeft, y);
  doc.text(`Date: ${today}`, pageWidth - marginRight, y, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  y += 4;

  // ===== SECTION: PARTIES =====
  doc.setFillColor(22, 45, 87);
  doc.rect(marginLeft, y, contentWidth, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('  PARTIES CONTRACTANTES', marginLeft + 3, y + 5);
  doc.setTextColor(0, 0, 0);
  y += 10;

  // Prestataire
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(22, 45, 87);
  doc.text('Le Prestataire :', marginLeft + 5, y);
  doc.setTextColor(0, 0, 0);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('BOUCHAIR ABDELOUAHAB', marginLeft + 10, y);
  y += 4;
  doc.setFontSize(8.5);
  doc.text("Bureau d'etudes et de consultation en Informatique", marginLeft + 10, y);
  y += 4;
  doc.text('11 Rue Rekaif Ali, Setif | Tel: 0661702451 | contact@winalco.com', marginLeft + 10, y);
  y += 7;

  // Client
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(22, 45, 87);
  doc.text('Le Client :', marginLeft + 5, y);
  doc.setTextColor(0, 0, 0);
  y += 5;

  y = addLabelValue('Nom :', contrat.client_name, y);
  if (contrat.telephone) {
    y = addLabelValue('Telephone :', contrat.telephone, y);
  }
  if (contrat.email) {
    y = addLabelValue('Email :', contrat.email, y);
  }
  if (contrat.wilaya || contrat.ville) {
    const location = [contrat.ville, contrat.wilaya].filter(Boolean).join(', ');
    y = addLabelValue('Adresse :', location, y);
  }
  y += 2;

  // ===== SECTION: DETAILS DU CONTRAT =====
  doc.setFillColor(22, 45, 87);
  doc.rect(marginLeft, y, contentWidth, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('  DETAILS DU CONTRAT', marginLeft + 3, y + 5);
  doc.setTextColor(0, 0, 0);
  y += 10;

  y = addLabelValue('Type de contrat :', contrat.t_contrat, y);
  y = addLabelValue('Mode reglement :', contrat.mode_reglt, y);
  y = addLabelValue('Date de debut :', dateDebut, y);
  y = addLabelValue('Date de fin :', dateFin, y);

  // Duration calculation
  const d1 = new Date(contrat.date_debut);
  const d2 = new Date(contrat.date_fin);
  const diffMonths = (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
  const duree = diffMonths >= 12
    ? `${Math.floor(diffMonths / 12)} an(s) et ${diffMonths % 12} mois`
    : `${diffMonths} mois`;
  y = addLabelValue('Duree :', duree, y);
  y += 2;

  // ===== SECTION: CONDITIONS =====
  doc.setFillColor(22, 45, 87);
  doc.rect(marginLeft, y, contentWidth, 7, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('  CONDITIONS GENERALES', marginLeft + 3, y + 5);
  doc.setTextColor(0, 0, 0);
  y += 10;

  const conditions = [
    'Article 1 - Le prestataire s\'engage a fournir les services de maintenance et support technique pour les logiciels specifies dans le present contrat.',
    'Article 2 - Le client s\'engage a regler les frais de service selon le mode de reglement convenu et dans les delais impartis.',
    'Article 3 - Le contrat prend effet a la date de debut mentionnee et expire a la date de fin, sauf renouvellement convenu entre les deux parties.',
    'Article 4 - En cas de resiliation anticipee, un preavis de 30 jours doit etre respecte par la partie souhaitant mettre fin au contrat.',
    'Article 5 - Le prestataire garantit la confidentialite des donnees du client et s\'engage a ne pas les divulguer a des tiers.',
  ];

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  conditions.forEach((condition) => {
    const lines = doc.splitTextToSize(condition, contentWidth - 10);
    // Check page overflow
    if (y + lines.length * 4 > 240) {
      doc.addPage();
      y = 20;
    }
    doc.text(lines, marginLeft + 5, y);
    y += lines.length * 4 + 1.5;
  });

  y += 2;

  // ===== SIGNATURES =====
  // Besoin ~45mm pour titres + cachet + signature + ligne
  if (y > 220) {
    doc.addPage();
    y = 20;
  }

  drawLine(y);
  y += 6;

  // Titres des deux parties
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Le Prestataire', marginLeft + 10, y);
  doc.text('Le Client', pageWidth - marginRight - 40, y);
  y += 4;

  // Lignes des deux contractants juste sous les titres
  doc.setDrawColor(150, 150, 150);
  doc.setLineWidth(0.3);
  doc.line(marginLeft + 5, y, marginLeft + 85, y);
  doc.line(pageWidth - marginRight - 65, y, pageWidth - marginRight - 5, y);

  // Labels "Signature et cachet" sous les lignes
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('Signature et cachet', marginLeft + 25, y + 4);
  doc.text('Signature et cachet', pageWidth - marginRight - 55, y + 4);
  doc.setTextColor(0, 0, 0);

  y += 8;

  // ===== Cachet + Griffe + Signature du prestataire (sous la ligne) =====
  // 1. Griffe / paraphe (~40x17mm)
  try {
    doc.addImage(GRIFFE_BASE64, 'PNG', marginLeft + 10, y - 1, 40, 17);
  } catch {
    // Ignore
  }
  // 2. Cachet (a gauche, rectangulaire 60x38mm)
  try {
    doc.addImage(CACHET_BASE64, 'PNG', marginLeft + 5, y + 3, 60, 38);
  } catch {
    // Ignore si l'image ne peut etre chargee
  }
  // 3. Signature manuscrite (sur le cachet, ~32x32mm)
  try {
    doc.addImage(SIGNATURE_BASE64, 'PNG', marginLeft + 10, y + 18, 32, 32);
  } catch {
    // Ignore
  }

  // Avancer y pour la fin de la section (espace reserve pour cachet/signature client a droite)
  y += 42;

  // ===== FOOTER =====
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerLegalY = pageHeight - 22;
  const colWidth = (pageWidth - marginLeft - marginRight) / 3;

  // Top line of footer
  doc.setDrawColor(22, 45, 87);
  doc.setLineWidth(0.5);
  doc.line(marginLeft, footerLegalY - 4, pageWidth - marginRight, footerLegalY - 4);

  // Legal info — 3 columns, 2 rows
  doc.setFontSize(7);
  doc.setTextColor(60, 60, 60);

  // ===== Row 1: CAPITAL | RC | ART =====
  // Col 1 - CAPITAL SOCIAL
  doc.setFont('helvetica', 'bold');
  doc.text('CAPITAL SOCIAL :', marginLeft, footerLegalY);
  doc.setFont('helvetica', 'normal');
  doc.text('0', marginLeft + 26, footerLegalY);

  // Col 2 - RC
  doc.setFont('helvetica', 'bold');
  doc.text('RC :', marginLeft + colWidth, footerLegalY);
  doc.setFont('helvetica', 'normal');
  doc.text('19/00-0481959 A 10', marginLeft + colWidth + 8, footerLegalY);

  // Col 3 - ART
  doc.setFont('helvetica', 'bold');
  doc.text('ART :', marginLeft + 2 * colWidth, footerLegalY);
  doc.setFont('helvetica', 'normal');
  doc.text('19013637081', marginLeft + 2 * colWidth + 9, footerLegalY);

  // ===== Row 2: NIS | NIF | RIB =====
  // Col 1 - NIS
  doc.setFont('helvetica', 'bold');
  doc.text('NIS :', marginLeft, footerLegalY + 4);
  doc.setFont('helvetica', 'normal');
  doc.text('197016180077917', marginLeft + 8, footerLegalY + 4);

  // Col 2 - NIF
  doc.setFont('helvetica', 'bold');
  doc.text('NIF :', marginLeft + colWidth, footerLegalY + 4);
  doc.setFont('helvetica', 'normal');
  doc.text('17016180077917400000', marginLeft + colWidth + 8, footerLegalY + 4);

  // Col 3 - RIB
  doc.setFont('helvetica', 'bold');
  doc.text('RIB :', marginLeft + 2 * colWidth, footerLegalY + 4);
  doc.setFont('helvetica', 'normal');
  doc.text('02100803113004857806', marginLeft + 2 * colWidth + 9, footerLegalY + 4);

  // ===== DOWNLOAD =====
  const fileName = `Contrat_${contrat.client_name.replace(/[^a-zA-Z0-9]/g, '_')}_CTR-${String(contrat.id).padStart(5, '0')}.pdf`;
  doc.save(fileName);
}
